import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { findLastIndex, slice, isNil } from 'lodash'
import { useNavigate } from 'features/i18n/router'

import { ROUTE_PATH } from 'lib/paths'
import { MODAL_MODE } from 'components/Modal/config'
import usePageModal from 'features/Modal/hooks/usePageModal'
import useGlobalConfirmBox from 'features/GlobalConfirmBox/hooks/useGlobalConfirmBox'
import {
  toggleBlockUserConfirmBox,
  toggleUnPinPostConfirmBox,
} from 'store/actions/confirmBox'
import { toggleJoinGroupBeforeActionConfirmBox } from 'store/actions/confirmBox'
import { useGetGroupQuery, useMuteUserMutation } from 'store/api/yay/group'
import {
  useCreateSurveysVoteMutation,
  useDeleteSelectedPostsMutation,
  useGetPostMutateMutation,
  useLikeMutation,
  useUnlikeMutation,
} from 'store/api/yay/post'
import { Post, PostCreated, PostRender } from 'lib/_types'
import {
  useAddHighlightMutation,
  useRemoveHighlightMutation,
} from 'store/api/yay/highlight'
import { togglePostBoxModal } from 'store/actions'
import { Dispatch, SetStateAction } from 'react'
import {
  useAddBookmarkMutation,
  useRemoveBookmarkMutation,
} from 'store/api/yay/bookmark'
import { addNotification } from 'store/slices/notifications'
import { useCheckAuthorizeBeforeAction } from 'hooks'
import { useBanUserMutation } from 'store/api/yay/group'
import {
  usePinnedPostGroupUpdateMutation,
  usePinnedPostUpdateMutation,
} from 'store/api/yay/timeline'
import { useCurrentUser } from 'store/api/yay/user'
import {
  groupToGroupContextSchema,
  postToPostContextSchema,
  snowplowEvent,
  userToUserContextSchemaData,
} from 'lib/analytics'

export const usePostActions = ({
  groupId,
  postList,
  setter,
  appendReply,
  appendReplyAsFirst,
  appendReplyAlternateSetter,
  onReplyCallback,
  onDeleteCallback,
}: {
  groupId?: string
  postList?: (Post | PostRender)[]
  setter?: Dispatch<SetStateAction<Post[]>>
  appendReply?: boolean
  appendReplyAsFirst?: boolean
  appendReplyAlternateSetter?: Dispatch<SetStateAction<Post[]>>
  onReplyCallback?: (createdPostData: {
    createdPost: PostCreated
    parentPost: Post
  }) => void
  onDeleteCallback?: Function
}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { showConfirmBox } = useGlobalConfirmBox()
  const { generatePageModalUrl } = usePageModal()
  const checkAuthorizeBeforeAction = useCheckAuthorizeBeforeAction()

  const currentUserId = useCurrentUser()?.data?.id

  const [like] = useLikeMutation()
  const [unlike] = useUnlikeMutation()
  const [deleteSelectedPosts] = useDeleteSelectedPostsMutation({
    fixedCacheKey: 'deleteSelectedPosts',
  })
  const [highlight] = useAddHighlightMutation()
  const [unHighlight] = useRemoveHighlightMutation()
  const [bookmark] = useAddBookmarkMutation()
  const [unBookmark] = useRemoveBookmarkMutation()
  const [banUser] = useBanUserMutation()
  const [pinnedPostGroupUpdate] = usePinnedPostGroupUpdateMutation()
  const [pinnedPostUpdate] = usePinnedPostUpdateMutation()
  const [createSurveysVote] = useCreateSurveysVoteMutation()
  const [getPostMutate] = useGetPostMutateMutation()
  const [muteUser] = useMuteUserMutation()

  const groupRes = useGetGroupQuery({ groupId }, { skip: isNil(groupId) })

  const isJoinedGroup = groupRes?.data?.is_joined

  const checkBeforeAction = checkAuthorizeBeforeAction((callback: Function) => {
    if (groupId && !isJoinedGroup) {
      dispatch(toggleJoinGroupBeforeActionConfirmBox(groupId))

      return
    }

    return callback()
  })

  const getEventParams = (post: Post) => {
    const index = postList.findIndex(
      (p) => (p['id'] || p['post']?.id) === post.id
    )

    return {
      post: postToPostContextSchema({
        post,
        position: index > -1 ? index + 1 : undefined,
        location: groupRes.data ? 'group' : 'timeline',
      }),
      group: groupToGroupContextSchema({
        group: groupRes.data,
        currentUserId,
      }),
    }
  }

  const _postSetter = (post: Post, attrs: object) => {
    setter?.((prevPosts: Array<Post>) => {
      return prevPosts?.map?.((y) => {
        if (y?.id == post?.id) {
          return {
            ...y,
            ...attrs,
          }
        }

        return y
      })
    })
  }

  const _resultSetter = ({
    posts,
    replacingData,
  }: {
    posts: Array<Post>
    replacingData: Partial<Post>
  }) => {
    return (posts ?? [])?.map?.((x) => {
      if (x?.id == replacingData?.id) {
        return {
          ...x,
          in_reply_to_post_count: x?.in_reply_to_post_count + 1,
          ...replacingData,
        }
      }

      return x
    })
  }

  const onReply = (post: Post) => {
    checkBeforeAction(() => {
      snowplowEvent.reply_post(getEventParams(post))

      return dispatch(
        togglePostBoxModal({
          post,
          mode: 'reply',
          onPostCreated: async (createdPostData: {
            createdPost: PostCreated
            parentPost: Post
          }) => {
            if (onReplyCallback) {
              onReplyCallback?.(createdPostData)
            } else if (appendReply) {
              // `createdPostData?.createdPost` is just a skeleton of a post.
              // So we'll need a proper post here.
              const _createdPost = (
                await getPostMutate({
                  postId: createdPostData?.createdPost?.id?.toString?.(),
                })
              )?.['data']?.post

              setter?.((prevState) => {
                // If parent post is not a conversation post before,
                // we'll need to update it to be one too.
                const updatedPrevState = _resultSetter({
                  posts: prevState ?? [],
                  replacingData: {
                    id: createdPostData?.parentPost?.id,
                    conversation_id: _createdPost?.conversation_id,
                  },
                })

                if (appendReplyAsFirst) {
                  return [_createdPost, ...updatedPrevState]
                }

                // To not make the timeline fluctuate when replying to a post
                // (i.e. moving the replying post group to the top of the timeline),
                // We'll append created post right after its parent.
                const parentPostIndex = findLastIndex(
                  prevState,
                  (x) => x?.id == createdPostData?.parentPost?.id
                )

                return [
                  ...slice(updatedPrevState, 0, parentPostIndex + 1),
                  _createdPost,
                  ...slice(
                    updatedPrevState,
                    parentPostIndex + 1,
                    updatedPrevState?.length
                  ),
                ]
              })

              // There is a time when we need an alternate setter,
              // i.e. when replying to pinned posts or conversation root posts.
              if (appendReplyAlternateSetter) {
                appendReplyAlternateSetter((prevState) => {
                  return _resultSetter({
                    posts: prevState,
                    replacingData: {
                      id: createdPostData?.parentPost?.id,
                    },
                  })
                })
              }
            } else {
              setter?.((prevState) => {
                return _resultSetter({
                  posts: prevState,
                  replacingData: {
                    id: createdPostData?.parentPost?.id,
                  },
                })
              })
            }
          },
        })
      )
    })
  }

  const onRepost = (post: Post) => {
    checkBeforeAction(() => {
      const shareablePost = post?.shareable?.post
      const targetPost = shareablePost?.id ? shareablePost : post

      snowplowEvent.repost_post(getEventParams(targetPost))

      return dispatch(
        togglePostBoxModal({
          post: targetPost,
          groupId,
          mode: 'repost',
        })
      )
    })
  }

  const onToggleLike = (post: Post) => {
    checkBeforeAction(() => {
      _postSetter(post, {
        _isLoading: true,
      })

      if (post?.liked) {
        unlike({ postId: post?.id }).then((res) => {
          if (res?.['data']?.response?.result == 'success') {
            _postSetter(post, {
              _isLoading: false,
              _likedRecently: false,
              liked: false,
              likes_count: post?.likes_count - 1,
            })
          }
        })
      } else {
        like({ postId: post?.id }).then((res) => {
          if (res?.['data']?.result == 'success') {
            snowplowEvent.like_post(getEventParams(post))

            _postSetter(post, {
              _isLoading: false,
              _likedRecently: true,
              liked: true,
              likes_count: post?.likes_count + 1,
            })
          }
        })
      }
    })
  }

  const onDeletePost = (post: Post) => {
    showConfirmBox({
      title: t('post.confirm_delete_action'),
      confirmBoxProps: {
        onOk: () => {
          deleteSelectedPosts([post?.id]).then((res) => {
            if (res?.['data']?.response?.result == 'success') {
              if (onDeleteCallback) {
                onDeleteCallback()
              } else if (post?._pinned) {
                // Deleting pinned post makes you deal with both
                // `__posts` and `__pinnedPosts`, so just reload the page
                // for simplicity.
                window.location.reload()
              } else {
                setter?.((prevPosts: Array<Post>) => {
                  return prevPosts?.filter?.((y) => y?.id != post?.id)
                })
              }
            }
          })
        },
      },
    })
  }

  const onBlockUser = (post: Post, index?: number) => {
    dispatch(
      toggleBlockUserConfirmBox({
        user: post?.user,
        post,
        postIndex: index,
        returnPath: null,
      })
    )
  }

  const onBanUser = (post: Post) => {
    showConfirmBox({
      title: t('post.actions.confirm_ban'),
      confirmBoxProps: {
        onOk: () => {
          banUser({
            groupId: post?.group_id,
            userId: post?.user?.id,
          }).then(() => {
            snowplowEvent.ban_group_user({
              ...getEventParams(post),
              user: userToUserContextSchemaData({
                user: post.user,
              }),
            })
          })
        },
      },
    })
  }

  const onHighlight = async (post: Post) => {
    if (!groupId) return

    _postSetter(post, {
      highlighted: true,
    })

    try {
      await highlight({ groupId: post?.group_id?.toString?.(), id: post.id })

      dispatch(
        addNotification({
          message: t('post.actions.highlight_added_notification'),
        })
      )
    } catch (e) {}
  }

  const onUnHighlight = (post: Post) => {
    if (!groupId) return

    _postSetter(post, {
      highlighted: false,
    })

    return unHighlight({ groupId: post?.group_id?.toString?.(), id: post.id })
  }

  const onBookmark = async (post: Post) => {
    _postSetter(post, {
      bookmarked: true,
    })

    try {
      await bookmark({ userId: currentUserId, id: post.id })

      dispatch(
        addNotification({
          message: t('post.actions.bookmark_added_notification'),
        })
      )
    } catch (e) {}
  }

  const onUnBookmark = (post: Post) => {
    _postSetter(post, {
      bookmarked: false,
    })

    return unBookmark({ userId: currentUserId, id: post.id })
  }

  const onPinPost = async (post: Post) => {
    try {
      if (groupId) {
        await pinnedPostGroupUpdate({
          postId: post?.id,
          groupId: post?.group_id,
        })
      } else {
        await pinnedPostUpdate({
          postId: post?.id,
        })
      }
    } finally {
      // Pinning post makes you deal with both `__posts` and `__pinnedPosts`, so just reload the page
      // for simplicity. Especially when in groups which you can only pin one post.
      window.location.reload()
    }
  }

  const onUnpinPost = (post: Post) => {
    dispatch(
      toggleUnPinPostConfirmBox({
        post,
        groupId,
        onUnpinPostCallback: () => {
          // Pinning post makes you deal with both `__posts` and `__pinnedPosts`, so just reload the page
          // for simplicity. Especially when in groups which you can only pin one post.
          window.location.reload()
        },
      })
    )
  }

  const onVote = ({ post, choiceId }: { post: Post; choiceId: number }) => {
    createSurveysVote({
      postId: post?.id,
      surveyId: post?.survey?.id,
      choiceId,
    }).then((res) => {
      if (res?.['data']?.result == 'success') {
        getPostMutate({
          postId: post?.id?.toString?.(),
        }).then((res2) => {
          _postSetter(post, {
            survey: res2?.['data']?.post?.survey,
          })
        })
      }
    })
  }

  const onMuteUser = (post: Post) => {
    showConfirmBox({
      title: t('post.actions.confirm_mute.title'),
      content: t('post.actions.confirm_mute.description'),
      confirmBoxProps: {
        confirmButtonText: t('common.mute'),
        onOk: async () => {
          await muteUser({
            groupId: post?.group_id,
            userId: post?.user?.id,
          })

          navigate(
            generatePageModalUrl(
              MODAL_MODE.GROUP_MUTE_LIST,
              {
                groupId: post?.group_id,
              },
              ROUTE_PATH.GROUP_DETAIL(post?.group_id)
            )
          )
        },
      },
    })
  }

  return {
    checkBeforeAction,
    onToggleLike,
    onDeletePost,
    onBlockUser,
    onBanUser,
    onHighlight,
    onUnHighlight,
    onReply,
    onRepost,
    onBookmark,
    onUnBookmark,
    onPinPost,
    onUnpinPost,
    onVote,
    onMuteUser,
  }
}
